import { useEffect, useState, useCallback, useRef } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, ToggleLeft, ToggleRight } from 'lucide-react';
import { Listbox } from '@headlessui/react';
import Card from '../components/Card';
import Button from '../components/Button';
import { useToastNotifications } from '../hooks/useToast';
import { realAPI } from '../api/realEndpoints';
import { formatDate } from '../utils/helpers';
import './ContentManagement.css';

interface SubscriptionPlan {
  id: number;
  planName: string;
  description: string;
  price: number;
  status: 'active' | 'inactive';
  type: string;
  deviceType: string;
  duration: number;
  createdAt: string;
}

const deviceOptions = [
  { label: 'All Devices', value: 'all' },
  { label: 'iOS', value: 'ios' },
  { label: 'Android', value: 'android' },
];

const typeOptions = [
  { label: 'All Types', value: 'all' },
  { label: 'Monthly', value: 'Monthly' },
  { label: 'Yearly', value: 'Yearly' },
  { label: 'LifeTime', value: 'LifeTime' },
  { label: 'Trial', value: 'trial' },
  { label: 'Life Time', value: 'life_time' },
];

export default function SubscriptionPlans() {
  const { success, error } = useToastNotifications();
  const errorRef = useRef(error);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [deviceFilter, setDeviceFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const limit = 10;

  useEffect(() => {
    errorRef.current = error;
  }, [error]);

  const fetchPlans = useCallback(async () => {
    setIsLoading(true);
    try {
      const offset = (page - 1) * limit;
      const resp = await realAPI.subscriptionPlans.getPlans(
        limit,
        offset,
        deviceFilter,
        typeFilter
      );
      const results = resp?.results || [];
      setPlans(results);
      setHasNext(results.length === limit);
    } catch (err) {
      errorRef.current?.('Failed to load plans', 'Please try again');
      setPlans([]);
      setHasNext(false);
    } finally {
      setIsLoading(false);
    }
  }, [page, limit, deviceFilter, typeFilter]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const handleToggleStatus = async (plan: SubscriptionPlan) => {
    const newStatus: 'active' | 'inactive' = plan.status === 'active' ? 'inactive' : 'active';
    setPlans((prev) => prev.map((p) => (p.id === plan.id ? { ...p, status: newStatus } : p)));
    try {
      await realAPI.subscriptionPlans.updateStatus(plan.id, newStatus);
      success(`Plan marked as ${newStatus}`, 'Status updated successfully');
    } catch (err) {
      setPlans((prev) => prev.map((p) => (p.id === plan.id ? { ...p, status: plan.status } : p)));
      error('Failed to update status', 'Change reverted');
    }
  };

  const TableHeader = () => (
    <div className="grid grid-cols-[1.1fr_2.6fr_0.7fr_0.7fr_0.7fr_0.7fr_0.8fr_0.8fr_0.8fr] gap-2 p-4 pl-3 rounded-t-xl font-semibold text-gray-700 text-sm border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 dark:text-white dark:border-gray-600 shadow-sm">
      <div className="text-gray-700 dark:text-white">Plan</div>
      <div className="text-gray-700 dark:text-white">Description</div>
      <div className="text-gray-700 dark:text-white">Price</div>
      <div className="text-gray-700 dark:text-white">Type</div>
      <div className="text-gray-700 dark:text-white">Device</div>
      <div className="text-gray-700 dark:text-white">Duration</div>
      <div className="text-gray-700 dark:text-white text-center">Created At</div>
      <div className="text-gray-700 dark:text-white">Status</div>
      <div className="text-gray-700 dark:text-white text-center">Actions</div>
    </div>
  );

  return (
    <div className="overflow-hidden">
      <Card className="mb-3 p-[10px]">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h1 className="text-2xl font-bold text-gray-900">Subscription Plans</h1>
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative w-[170px]">
              <Listbox
                value={deviceFilter}
                onChange={(v) => {
                  setDeviceFilter(v);
                  setPage(1);
                }}
              >
                <Listbox.Button className="w-full h-10 pl-4 pr-10 text-left bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all duration-200 hover:border-gray-400 flex items-center overflow-hidden">
                  <span className="block truncate">
                    {deviceOptions.find((o) => o.value === deviceFilter)?.label || 'Device'}
                  </span>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </Listbox.Button>
                <Listbox.Options className="absolute z-20 mt-2 w-full bg-white rounded-lg shadow-lg border border-gray-200">
                  {deviceOptions.map((option) => (
                    <Listbox.Option
                      key={option.value}
                      value={option.value}
                      className={({ active }) =>
                        `px-4 py-2 cursor-pointer transition-colors ${active ? 'bg-gray-100' : ''}`
                      }
                    >
                      {option.label}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Listbox>
            </div>

            <div className="relative w-[170px]">
              <Listbox
                value={typeFilter}
                onChange={(v) => {
                  setTypeFilter(v);
                  setPage(1);
                }}
              >
                <Listbox.Button className="w-full h-10 pl-4 pr-10 text-left bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all duration-200 hover:border-gray-400 flex items-center overflow-hidden">
                  <span className="block truncate">
                    {typeOptions.find((o) => o.value === typeFilter)?.label || 'Plan Type'}
                  </span>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </Listbox.Button>
                <Listbox.Options className="absolute z-20 mt-2 w-full bg-white rounded-lg shadow-lg border border-gray-200">
                  {typeOptions.map((option) => (
                    <Listbox.Option
                      key={option.value}
                      value={option.value}
                      className={({ active }) =>
                        `px-4 py-2 cursor-pointer transition-colors ${active ? 'bg-gray-100' : ''}`
                      }
                    >
                      {option.label}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Listbox>
            </div>

            <Button
              variant="outline"
              className="h-10"
              onClick={() => {
                setDeviceFilter('all');
                setTypeFilter('all');
                setPage(1);
              }}
            >
              Reset Filters
            </Button>
          </div>
        </div>
      </Card>

      <Card>
        {isLoading ? (
          <div className="table-container">
            <div className="table-min-w">
              <TableHeader />
              <div className="flex flex-col items-center justify-center py-12">
                <div className="loader" />
              </div>
            </div>
          </div>
        ) : plans.length === 0 ? (
          <div className="table-container">
            <div className="table-min-w">
              <TableHeader />
              <div className="text-center py-12 bg-gray-50 rounded-b-xl">
                <img src="/NoRecord.svg" alt="No records found" className=" mx-auto mb-4 " />
              </div>
            </div>
          </div>
        ) : (
          <div className="table-container">
            <div className="table-min-w">
              <TableHeader />
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {plans.map((plan) => (
                  <div
                    key={plan.id}
                    className="grid grid-cols-[1.1fr_2.6fr_0.7fr_0.7fr_0.7fr_0.7fr_0.8fr_0.8fr_0.8fr] gap-2 p-4 pl-3 hover:bg-blue-50 dark:hoverbg-gray-700/50 transition-colors items-start"
                  >
                    <div className="font-semibold text-gray-900 dark:text-white">
                      {plan.planName}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      <div
                        className={`${expanded[plan.id] ? '' : 'line-clamp-2'} whitespace-pre-wrap break-words`}
                      >
                        {plan.description}
                      </div>
                      {plan.description && plan.description.length > 120 && (
                        <button
                          type="button"
                          onClick={() =>
                            setExpanded((prev) => ({ ...prev, [plan.id]: !prev[plan.id] }))
                          }
                          className="mt-1 text-xs font-semibold text-primary-600 hover:text-primary-700"
                        >
                          {expanded[plan.id] ? 'See less' : 'See more'}
                        </button>
                      )}
                    </div>
                    <div className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                      £{plan.price.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
                    </div>
                    <div className="text-sm text-gray-700 dark:text-gray-300">{plan.type}</div>
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      {plan.deviceType}
                    </div>
                    <div className="text-sm text-gray-700 dark:text-gray-300">{plan.duration}</div>
                    <div className="text-sm text-gray-700 dark:text-gray-300 text-center">
                      {formatDate(plan.createdAt)}
                    </div>
                    <div className="text-sm">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-md capitalize ${
                          plan.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {plan.status}
                      </span>
                    </div>
                    <div className="flex justify-center">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleToggleStatus(plan);
                        }}
                        className={`p-2 rounded-lg transition-colors ${
                          plan.status === 'active'
                            ? 'text-green-600 hover:bg-green-50'
                            : 'text-red-600 hover:bg-red-50'
                        }`}
                        title={`Mark as ${plan.status === 'active' ? 'inactive' : 'active'}`}
                      >
                        {plan.status === 'active' ? (
                          <ToggleRight className="w-6 h-6" />
                        ) : (
                          <ToggleLeft className="w-6 h-6" />
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {plans.length > 0 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-gray-500">Page {page}</p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="w-4 h-4" />
                Prev
              </Button>
              <Button variant="outline" onClick={() => setPage((p) => p + 1)} disabled={!hasNext}>
                Next
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
